import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { LayoutDashboard, CalendarDays, Users, Loader2, Video } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { LessonCard } from "@/components/LessonCard";
import { api } from "@/lib/api";

const nav = [
  { label: "Schedule", href: "#schedule", icon: CalendarDays },
  { label: "Students", href: "#students", icon: Users },
];

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between"><p className="text-sm text-slate-500">{label}</p><Icon className="h-5 w-5 text-[#BA8A30]" /></div>
      <p className="font-display text-3xl font-bold text-[#595C2B] mt-2">{value}</p>
    </div>
  );
}

export default function TutorDashboard() {
  const { data: lessons = [], isLoading } = useQuery({ queryKey: ["lessons"], queryFn: async () => (await api.get("/lessons")).data });
  const confirmed = lessons.filter((l) => l.payment_status === "paid");
  const students = [...new Set(confirmed.map((l) => l.child_name))];

  return (
    <DashboardShell title="Tutor Dashboard" subtitle="Your schedule and students" nav={nav}>
      <div className="grid sm:grid-cols-3 gap-6 mb-10">
        <Kpi label="Confirmed lessons" value={confirmed.length} icon={CalendarDays} />
        <Kpi label="Unique students" value={students.length} icon={Users} />
        <Kpi label="Total booked" value={lessons.length} icon={LayoutDashboard} />
      </div>

      <section id="schedule" className="mb-10">
        <h2 className="font-display text-2xl font-semibold text-[#595C2B] mb-4">Upcoming classes</h2>
        {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-[#595C2B]" /> : lessons.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
            <Video className="h-8 w-8 mx-auto text-slate-300 mb-2" />No lessons scheduled with you yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {lessons.map((l, i) => (
              <motion.div key={l.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <LessonCard lesson={l} who="tutor" />
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <section id="students">
        <h2 className="font-display text-2xl font-semibold text-[#595C2B] mb-4">My students</h2>
        {students.length === 0 ? <p className="text-slate-500">No students yet.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {students.map((s) => (
              <div key={s} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`student-${s}`}>
                <p className="font-semibold text-[#595C2B]">{s}</p>
                <p className="text-sm text-slate-500">{confirmed.filter((l) => l.child_name === s).length} lesson(s)</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </DashboardShell>
  );
}
